<template>
    <VJoyPanel v-if="isVisibleSkillSetSection" :id="anchorsPageProfile.skillSetSection" data-testid="profile-main-skill-set-section" padding="large">
        <template #panel-title>
            <h2 class="profile-skill-set__title">
                {{ t('profile.section.skill.set.title') }}
                <span v-if="showSkillCountInSectionTitle" data-testid="profile-main-skill-set-title-counter">({{ countSelectedAndTopSkills }})</span>
            </h2>
        </template>
        <template #panel-title-action>
            <TranslateButton v-if="showTranslationButton" section="skill_set" />
            <VJoyIconButton
                v-if="editMode && hasSelectedSkills"
                variant="ghost"
                icon="pen"
                size="small"
                data-testid="profile-main-skill-set-cta-edit"
                :label="$t('general.edit')"
                @click="openSkillSetEditDialog"
            />
        </template>

        <template #panel-body>
            <ShowMoreByMaxHeight
                v-if="hasSelectedSkills"
                test-id="toggle-show-all-skill-set"
                :label="t('profile.section.skill.set.show.all.label.v2', {count: profileSelectedSkills.length})"
                :display-all-in-edition-mode="false"
                :max-height="200"
                :locked="{
                    allowHref: true,
                    label: t('profile.section.expertises.private', [profile?.firstname]),
                    componentName: 'profile-skill-set-section',
                    targetId: anchorsPageProfile.skillSetSection,
                }"
            >
                <VJoyWrapper v-bind="{direction: 'column', align: 'flex-start'}" no-margin data-testid="profile-main-skill-set">
                    <SkillsReadOnly
                        data-testid="profile-main-skill-set-selected-skills"
                        :skills="profileSelectedSkills"
                        :is-visible-seo-link="isVisibleSeoLink"
                        :is-visible-translated-label="isVisibleTranslatedLabel"
                    />
                </VJoyWrapper>
            </ShowMoreByMaxHeight>
            <EmptyContentSection
                v-else-if="editMode"
                data-testid="profile-main-skill-set-empty-state"
                icon="list-file"
                :title="$t('profile.section.skill.set.empty.state.title')"
                :description="$t('profile.section.skill.set.empty.state.description')"
            >
                <template #action>
                    <ClientOnly>
                        <VJoyButton
                            icon="add"
                            size="small"
                            variant="secondary"
                            data-testid="profile-main-skill-set-cta-edit"
                            @click="openSkillSetEditDialog"
                        >
                            {{ $t('profile.section.skill.set.empty.state.cta') }}
                        </VJoyButton>
                        <template #fallback>
                            <VJoyFormFieldSkeleton size="small" width="250px" />
                        </template>
                    </ClientOnly>
                </template>
            </EmptyContentSection>
            <EmptyContentSection
                v-else
                icon="conditions"
                :title="$t('profile.section.skill.set.empty.title')"
                :description="$t('profile.section.skill.set.empty.description')"
            />
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {onMounted, onUnmounted, useTranslation} from '#imports';
    import {VJoyButton, VJoyIconButton, VJoyPanel, VJoyWrapper, VJoyFormFieldSkeleton} from '@maltjoy/core-vue';
    import ShowMoreByMaxHeight from '~/components/_common/show-more/ShowMoreByMaxHeight.vue';
    import TranslateButton from '~/components/main/TranslateButton.vue';
    import EmptyContentSection from '~/components/section/EmptyContentSection.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import {useTranslationStore} from '~/store/translation/translationStore';
    import {ProfileVertical} from '~/types/domain/Profile';
    import SkillsReadOnly from '@/components/_common/skills/SkillsReadOnly.vue';
    import {useSkillSetStore} from '@/store/skill-set/skillSetStore';

    const {t} = useTranslation();
    const {fetchSkillSet} = useSkillSetStore();
    const {profileSelectedSkills, profileTopSkills} = storeToRefs(useSkillSetStore());
    const {isTranslationActivated, isTranslationDone} = storeToRefs(useTranslationStore());
    const {profile} = storeToRefs(useProfileStore());
    const editMode = computed(() => profile.value?.editMode);

    const isVisibleSkillSetSection = computed(() => editMode.value || hasSelectedSkills.value);
    const showTranslationButton = computed(() => isTranslationActivated.value);

    // Top Skills
    const hasTopSkills = computed(() => profileTopSkills.value && profileTopSkills.value.length > 0);

    // Selected Skills
    const hasSelectedSkills = computed(() => profileSelectedSkills.value && profileSelectedSkills.value.length > 0);
    const countSelectedAndTopSkills = computed(() => profileTopSkills.value.length + profileSelectedSkills.value.length);
    const showSkillCountInSectionTitle = computed(() => !editMode.value && !hasTopSkills.value && hasSelectedSkills.value);
    const isVisibleSeoLink = computed(() => profile.value?.vertical === ProfileVertical.FREELANCER);
    const isVisibleTranslatedLabel = computed((): boolean => isTranslationActivated.value && isTranslationDone.value);

    onMounted(() => {
        eventBus.on('expertises-skills:updated', refreshSkills);
        eventBus.on('skill-set:updated', refreshSkills);
    });

    onUnmounted(() => {
        eventBus.off('expertises-skills:updated', refreshSkills);
        eventBus.off('skill-set:updated', refreshSkills);
    });

    async function refreshSkills(): Promise<void> {
        await fetchSkillSet();
    }

    function openSkillSetEditDialog() {
        eventBus.emit('skill-set:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }
</script>

<style lang="scss" scoped>
    .profile-skill-set {
        &__title {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-2);

            span {
                flex-grow: 0;
            }
        }

        &__content {
            &__header {
                padding-bottom: var(--joy-core-spacing-5);
                font-weight: var(--joy-font-weight-bold);
            }
        }
    }
</style>
